import { faker } from "@faker-js/faker";
import { v7 as randomUUID } from "uuid";
import { create } from "zustand";

import { INewExam, INewQuestionOption, Question } from "@/@types";
import { ENV } from "@/env";

// Tipo local para questões no formulário
interface LocalQuestion {
  id: string;
  description: string;
}

// Tipo para questões que podem vir do service ou ser locais
interface FormQuestion extends LocalQuestion {
  isLocal?: boolean;
}

interface FormLessonStoreValues extends Omit<INewExam, "questions"> {
  loading: boolean;
  error?: string;
  selectedQuestion?: FormQuestion;
  questions: FormQuestion[];
  options: INewQuestionOption[];
  assessmentData?: any; // Dados do assessment carregado
}

interface FormLessonStore extends Partial<FormLessonStoreValues> {
  setLoading(value: boolean): void;
  setError(message?: string): void;
  reset(): void;
  setSelectedQuestion(id: string): void;
  set(values: Partial<FormLessonStoreValues>): void;
  addQuestion(): void;
  setQuestion(questionId: string, newValues: Partial<FormQuestion>): void;
  addOption(questionId: string): void;
  setOption(optionId: string, newValues: Partial<INewQuestionOption>): void;
  deleteQuestion(questionId: string): void;
  deleteOption(optionId: string): void;
  loadQuestionsFromService(questions: Question[]): void;
  setAssessmentData(data: any): void;
  clearOptionsForQuestion(questionId: string): void;
}

export const useFormLesson = create<FormLessonStore>((set) => ({
  loading: false,
  error: undefined,
  selectedQuestion: undefined,
  questions: [],
  options: [],
  assessmentData: undefined,

  setLoading(value) {
    set({ loading: value });
  },

  setError(message) {
    set({ error: message });
  },

  set(values) {
    set((prevValues) => ({ ...prevValues, ...values }));
  },

  reset() {
    set({
      loading: false,
      error: undefined,
      selectedQuestion: undefined,
      questions: [],
      options: [],
      assessmentData: undefined,
    });
  },

  setAssessmentData(data) {
    set({ assessmentData: data });
  },

  loadQuestionsFromService(questions: Question[]) {
    set((prevValues) => {
      const newQuestions: FormQuestion[] = questions.map((question) => ({
        id: question.id,
        description: question.description,
        isLocal: false,
      }));

      return {
        ...prevValues,
        questions: newQuestions,
      };
    });
  },

  addQuestion() {
    set((prevValues) => {
      const id = `temp-${randomUUID()}`;
      let description = "";
      if (ENV.VITE_NODE_ENV === "development") description = faker.lorem.text();

      const newQuestion: FormQuestion = {
        id,
        description,
        isLocal: true,
      };

      if (prevValues.questions)
        return {
          ...prevValues,
          questions: [...prevValues.questions, newQuestion],
        };

      return { ...prevValues, questions: [newQuestion] };
    });
  },

  addOption(questionId) {
    set((prevValues) => {
      const id = randomUUID();
      const description = ""; // Deixar vazio para o usuário escrever

      const prevOptions = [...(prevValues.options || [])];
      const newOption = {
        id,
        questionId,
        description,
        isCorrect:
          prevOptions.filter((option) => option.questionId === questionId)
            .length <= 0,
        isLocal: true, // Marcar como local para indicar que ainda não foi salva
      };

      const updatedOptions = [...prevOptions, newOption];

      return {
        ...prevValues,
        options: updatedOptions,
      };
    });
  },

  setSelectedQuestion(questionId) {
    set((prevValues) => {
      const question = prevValues.questions?.find(
        (item) => item.id === questionId
      );
      if (!question?.id) return prevValues;
      return { ...prevValues, selectedQuestion: question };
    });
  },

  setQuestion(questionId, newValues) {
    set((prevValues) => {
      const updatedQuestions = (prevValues.questions || []).map((item) => {
        if (item.id === questionId) return { ...item, ...newValues };
        return item;
      });

      return { ...prevValues, questions: updatedQuestions };
    });
  },

  setOption(optionId, newValues) {
    set((prevValues) => {
      console.log("=== setOption called ===");
      console.log("optionId:", optionId);
      console.log("newValues:", newValues);
      console.log("prevOptions:", prevValues.options);

      const updatedOptions = (prevValues.options || []).map((item) => {
        if (item.id === optionId) {
          const updatedItem = { ...item, ...newValues };
          console.log("🔄 Updating option:", item, "->", updatedItem);
          return updatedItem;
        }
        return item;
      });

      // Se a opção não existe, adicionar
      const optionExists = (prevValues.options || []).some(
        (item) => item.id === optionId
      );
      if (!optionExists) {
        console.log("➕ Adding new option:", { id: optionId, ...newValues });
        updatedOptions.push({
          id: optionId,
          ...newValues,
        } as INewQuestionOption);
      }

      console.log("📦 Final options array:", updatedOptions);

      return { ...prevValues, options: updatedOptions };
    });
  },

  deleteQuestion(questionId) {
    set((prevValues) => {
      const updatedQuestions = (prevValues.questions || []).filter(
        (item) => item.id !== questionId
      );

      // Limpar opções da questão deletada
      const updatedOptions = (prevValues.options || []).filter(
        (item) => item.questionId !== questionId
      );

      return {
        ...prevValues,
        questions: updatedQuestions,
        options: updatedOptions,
      };
    });
  },

  deleteOption(optionId) {
    set((prevValues) => {
      console.log("=== deleteOption called ===");
      console.log("optionId:", optionId);
      console.log("prevOptions:", prevValues.options);

      const updatedOptions = (prevValues.options || []).filter(
        (item) => item.id !== optionId
      );

      console.log("🗑️ Options after deletion:", updatedOptions);

      return { ...prevValues, options: updatedOptions };
    });
  },

  clearOptionsForQuestion(questionId) {
    set((prevValues) => {
      console.log("=== clearOptionsForQuestion called ===");
      console.log("questionId:", questionId);
      console.log("prevOptions:", prevValues.options);

      const updatedOptions = (prevValues.options || []).filter(
        (item) => item.questionId !== questionId
      );

      console.log("🧹 Options after clearing for question:", updatedOptions);

      return { ...prevValues, options: updatedOptions };
    });
  },
}));
